import {useState} from "react";
import axios from "axios";
import {sendEventToGA} from "./../utils.js";
import {redirect} from "react-router-dom";
import {Link, useNavigate, useLoaderData} from "react-router-dom";
import {Header} from "../layout/header.js";
import {Footer} from "../layout/footer.js";
import {BreadcrumbBar} from "../components/breadcrumbbar.js";
import {FolderTabs} from "../components/foldertabs.js";
import {SearchBar} from "../components/searchbar.js";
import {SearchResultsFacet} from "../components/searchresultsfacet.js";
import {SearchResultsTable} from "../components/searchresultstable.js";
import {parseFacetRestricts} from "./searchpage_utils.js";
import {filterNonZeroFacets} from "../components/searchresultsfacet_utils.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {library as fontAwesomeLibrary} from "@fortawesome/fontawesome-svg-core";
import {faSatellite, faTableList, faArrowCircleRight} from "@fortawesome/free-solid-svg-icons";
import hdsSkrimImg from "../images/hds-skrim.png";


/**
 * React-router-dom Loader function to load backend endpoint with
 * AJAX, the response from which drives the page.
 *
 * @param params Parameters of the page URL
 */
export async function loader({params}) {
    let pageNum = parseInt(params.pageNum);
    if (isNaN(pageNum)) {
        pageNum = 1;
    }
    try {
        const requestStart = Date.now();
        const getParams = new URLSearchParams(window.location.search);
        const scrollToTop = (getParams.get("scrollToTop") || "").toString() !== "0";
        const rawFacetRestricts = params.rawFacetRestricts || "";

        const response = await axios.get(
            `/heliodata/app/load_region_page/${params.slug}/${pageNum}?${rawFacetRestricts}`
        );
        const requestTimeToComplete = (Date.now() - requestStart) / 1000;

        let unfilteredResponse = null;
        let responseDataUnion = null;

        if(rawFacetRestricts === "") {
            responseDataUnion = response.data;
        } else {
            let missionsOnlyResponse = null;
            let allRestrictsResponse = null;

            unfilteredResponse = await axios.get(
                `/heliodata/app/load_region_page/${params.slug}/${pageNum}?${""}`
            );

            const missionsRestriction = rawFacetRestricts
                .split("&")
                .filter(param => param.startsWith("mission_facet_cs="))
                .join("&");


            if(missionsRestriction || missionsRestriction !== ""){
                missionsOnlyResponse = await axios.get(
                    `/heliodata/app/load_region_page/${params.slug}/${pageNum}?${missionsRestriction}`
                );
                allRestrictsResponse = await axios.get(
                    `/heliodata/app/load_region_page/${params.slug}/${pageNum}?${rawFacetRestricts}`
                );

                responseDataUnion = allRestrictsResponse.data;
                responseDataUnion.searchData.facets.facet_fields = filterNonZeroFacets(
                                                                       responseDataUnion.searchData.facets.facet_fields,
                                                                       ["measurement_type_cs", "eras", "cadence_cat", "region"]);
                responseDataUnion.searchData.facets.facet_fields.mission_facet_cs = unfilteredResponse.data.searchData.facets.facet_fields.mission_facet_cs;
                responseDataUnion.searchData.facets.facet_fields.instrument_facet_cs = filterNonZeroFacets(
                                                                                           missionsOnlyResponse.data.searchData.facets.facet_fields,
                                                                                           ["instrument_facet_cs"]).instrument_facet_cs;

                const missionFacet =
                     unfilteredResponse?.data?.searchData?.facets?.mission_facet_cs || [];
                responseDataUnion.searchData.facets.mission_facet_cs = missionFacet;
            } else{
                responseDataUnion = unfilteredResponse.data;
            }
        }

        return {
            regionData: response.data,
            regionId: params.slug,
            pageNum: pageNum,
            rawFacetRestricts: rawFacetRestricts,
            scrollToTop: scrollToTop,
            requestTimeToComplete,
            unfilteredData: responseDataUnion,
        };
    }
    catch (error) {
        return redirect("/error");
    }
}


/**
 * React component to display the Region page.
 *
 * @param props.regionData Backend response describing the region
 * @param props.regionID of the region node in the heliodata system.
 * @param props.pageNum Page number to display, which may be undefined.
 * @param props.rawFacetResricts Facet restrictions from the page URL,
 * which may be undefined.
 * @return JSX component
 */
export function RegionPageContent() {
    const {
      regionData,
      regionId,
      pageNum,
      rawFacetRestricts,
      scrollToTop,
      requestTimeToComplete,
      unfilteredData,
    } = useLoaderData();

    const navigate = useNavigate();
    const facetRestricts = parseFacetRestricts(rawFacetRestricts);

    // Collect breadcrumb items
    const breadcrumbLevels = [];

    regionData.parentRegions.forEach(({slug, title}) => {
        breadcrumbLevels.push({link: `/region/${slug}`, text: title});
    });

    breadcrumbLevels.push({link: `/region/${regionData.slug}`, text: regionData.title});

    // Override query string scroll to top; fixes bug where it is sometimes
    // not respected
    // eslint-disable-next-line
    let scrollToTopFixed = scrollToTop;

    if (pageNum !== 1) {
	scrollToTopFixed = false;
    }
    if (Object.keys(rawFacetRestricts).length !== 0) {
        // eslint-disable-next-line
	scrollToTopFixed = false;
    }

    const handleSubregionClick = (title) => {
        sendEventToGA(`region_page__sub_region__button`, 'sub-regions' , title);
    }

    const startsWithAny = (str, words) => {
        if (typeof str !== 'string' || !Array.isArray(words)) return false;
        const lowerStr = str.toLowerCase();
        return words.some(prefix => lowerStr.startsWith(prefix.toLowerCase()));
    }

    const rephraseMagnetosphere = (str) => {
        const index = str.lastIndexOf(" magnetosphere");
        if (index !== -1 && str.split(" ").length === 2 ) {
            const planet = str.slice(0, index).replace(/'s$/, ''); // Remove trailing 's if present
            return `the magnetosphere of ${planet}`;
        }
        return str;
    }

    // Set banner image
    let bannerStyle = {};
    if (regionData.bannerImage) {
      bannerStyle = {backgroundImage: `url(${hdsSkrimImg}), url(${regionData.bannerImage})`};
    }
    const planetNames = ["Mercury", "Venus", "Earth", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune"];
    const possessionLowerStr = startsWithAny(regionData.title, planetNames)
                          ?
                          (regionData.title[0].toUpperCase() + regionData.title.slice(1).toLowerCase()).split(" ")[0] + "'s " +
                          (regionData.title[0].toUpperCase() + regionData.title.slice(1).toLowerCase()).split(" ").slice(1).join(" ")
                          : "the " + regionData.title;

    let possessionUpperStr = startsWithAny(regionData.title, planetNames)
                          ?
                          (regionData.title[0].toUpperCase() + regionData.title.slice(1)).split(" ")[0] + "'s " +
                          (regionData.title[0].toUpperCase() + regionData.title.slice(1)).split(" ").slice(1).join(" ")
                          : "the " + regionData.title;
    possessionUpperStr = possessionUpperStr.replace("s's","s'");

    let firstLetterUpperStr = startsWithAny(regionData.title, planetNames)
                          ?
                          (regionData.title[0].toUpperCase() + regionData.title.slice(1)).split(" ")[0] + " " +
                          (regionData.title[0].toUpperCase() + regionData.title.slice(1).toLowerCase()).split(" ").slice(1).join(" ")
                          : "the " + regionData.title;

    let aboutSectionName = possessionUpperStr.includes("Earth's") ? possessionUpperStr : `the ${possessionUpperStr.replace("'s", "").replace("s'","s")}` ;
    aboutSectionName = aboutSectionName.includes("Atmosphere") && !aboutSectionName.includes("Lower and Middle Atmosphere") ? aboutSectionName.replace("Atmosphere", "Atmospheric") : aboutSectionName;

    if(!firstLetterUpperStr.includes("Earth")) {
        firstLetterUpperStr = rephraseMagnetosphere(firstLetterUpperStr);
    }

    const scrollInfo = firstLetterUpperStr.replace("Earth ", "Earth's ");

    return (
        <div>
          <Header scrollToTop={scrollToTop} />
          <div
            className="regionPageBanner"
            style={bannerStyle}>

            <BreadcrumbBar levels={breadcrumbLevels} />


            <div className="regionPageBannerTitle">
              {regionData.title} Data
            </div>
            {(regionData.childRegions.length > 0) &&
              <div className="regionPageBannerDescription">
                {regionData.tagline}
              </div>
            }
            <div className="regionPageBannerScrollMessage">
              Scroll down to explore data from featured heliophysics missions that study {(startsWithAny(regionData.title, planetNames) ? scrollInfo : "the " + regionData.title.toLowerCase()).replace("the planetary","planetary").replace("the interplanetary","interplanetary")}{regionData.childRegions.length > 0 ? (regionData.title.toLowerCase().endsWith("es") ? " and subregions." :  " and its subregions.") : "."}
            </div>
          </div>

          <h3 className="regionPageDescriptionTitle">About {aboutSectionName.replace("the the", "the")} {regionData.childRegions.length > 0 ? "Region" : aboutSectionName.endsWith("Subregion") ? "" : "Subregion"}</h3>
          <div className="regionPageDescription">
            <div className="regionPageDescriptionText">
              {regionData.bodyText.split('\n').map((para, index) => <div key={index} className="regionPageDescriptionParagraph" dangerouslySetInnerHTML={{__html: (para)}}/>)}
            </div>

            {regionData.bodySupportingImage &&
              <div className="regionPageSupportingImageWithCaption">
                <img
                  src={regionData.bodySupportingImage}
                  alt={regionData.title} />
                <div className="regionPageSupportingImageWithCaptionText" dangerouslySetInnerHTML={{__html: (regionData.bodySupportingImageCaption)}}/>
              </div>}
          </div>

	{regionData.childRegions.length > 0 &&
	    <div className="regionPageSubregionListContainer">
            <div className="regionPageSubregionList">
                {regionData
                 .childRegions
                 .map(subregion =>
                       <Link to={`/region/${subregion.slug}`}
                             className="regionPageSubregionCardOuter"
                             key={subregion.slug}
                             onClick={()=>handleSubregionClick(subregion.title)}>
			 <div className="regionPageSubregionCard">
			 <span className="regionPageSubregionCardLabel">
			   Subregion Data
    		         </span>
			 <br />
		         <span className="regionPageSubregionCardTitle">

                            {subregion.title}

			 </span>

			 <FontAwesomeIcon icon={faArrowCircleRight} className="regionPageSubregionCardIcon" />
			 </div>
		      </Link>
                     )}
              </div>
	 </div>}

          <div id="regionDiscoverAnchor" className="regionPageDiscoverBar">
            Discover {regionData.title} Data
          </div>

          <RegionPageDiscoverySection
            regionData={regionData}
            regionId={regionId}
            facetRestricts={facetRestricts}
            navigate={navigate}
            rawFacetRestricts={rawFacetRestricts}
            requestTimeToComplete={requestTimeToComplete}
            unfilteredData = {unfilteredData}
          />
          <Footer />
        </div>
    );
}

/**
 * Displays the content underneath the "Discover $regionName" divider.
 * Includes tabs and content under those tabs.
 * @prop regionData Backend information to drive the page.
 * @returns JSX element
 */
function RegionPageDiscoverySection(props) {
  const {
      regionData,
      regionId,
      facetRestricts,
      rawFacetRestricts,
      pageNum,
      requestTimeToComplete,
      navigate,
      unfilteredData
  } = props;


  const [selectedTab, setSelectedTab] = useState("featuredMissions");

  // Decide what content to show based on selected tab
  let visibleContent = <div />;

  if (selectedTab === "featuredMissions") {
    visibleContent = <FeaturedMissionSection regionData={regionData} />;
  } else if (selectedTab === "allDatasets") {
      visibleContent = (
          <RegionAllDatasets
            regionId={regionId}
            regionData={regionData}
            facetRestricts={facetRestricts}
            rawFacetRestricts={rawFacetRestricts}
            pageNum={pageNum}
            requestTimeToComplete={requestTimeToComplete}
            navigate={navigate}
            unfilteredData={unfilteredData}
            />
      );
  }

  const datasetCount = regionData.searchData.numFound;

  // The All Datasets tab is only offered when the region has datasets
  const tabs = [
    {key: "featuredMissions", label: "Featured Missions", icon: faSatellite},
    ...(datasetCount > 0
      ? [{key: "allDatasets", label: "All Datasets", icon: faTableList}]
      : []),
  ];

  // Load font awesome icons
  fontAwesomeLibrary.add(faSatellite);
  fontAwesomeLibrary.add(faTableList);
  fontAwesomeLibrary.add(faArrowCircleRight);

  // Return main JSX content with visibleContent embedded
  return (
    <div>
      <FolderTabs
        idPrefix="regionDiscovery"
        tabs={tabs}
        selected={selectedTab}
        onSelect={setSelectedTab}
        panelClassName="regionPageDiscoveryVisibleContent"
        scrollToId="regionDiscoverAnchor">
        {visibleContent}
      </FolderTabs>
    </div>
  );
}


/**
 * Display the featured missions section of the region page.
 *
 * @prop regionData Backend response for loading the region page.
 * @return JSX component
 */
function FeaturedMissionSection(props) {
  const {regionData} = props;

  const handleMissionClick = (missionName) => {
      sendEventToGA(`region_page__mission__button`, 'missions' , missionName);
  }

  if (props.regionData.featuredMissions.length === 0) {
      return <em>Featured missions for this region coming soon!</em>;
  }

  return (
    <table className="regionPageFeaturedMissionsTable">
      <tbody><tr className="regionPageFeaturedMissionsHeaderTr">
        <td className="regionPageFeaturedMissionsHeaderTd">Mission or Observatory</td>
        <td className="regionPageFeaturedMissionsHeaderTd regionPageFeaturedMissionsOptionalCol">
          First Available Data
        </td>
        <td className="regionPageFeaturedMissionsHeaderTd regionPageFeaturedMissionsOptionalCol">
          Last Available Data
        </td>
        <td className="regionPageFeaturedMissionsHeaderTd regionPageFeaturedMissionsOptionalCol">
          Dataset Count
        </td>
      </tr>
      {regionData.featuredMissions.map(
          (mission, i) =>
          <tr className={"regionPageFeaturedMissionsNormalTr" + (i%2)} key={i}>
            <td className="regionPageFeaturedMissionsNormalTd">
              <strong><Link to={`/mission/${mission.id}`} onClick = {()=>handleMissionClick(mission.altName || mission.name)} >{mission.altName || mission.name}</Link></strong>
              <br />{mission.tagline}
            </td>
            <td className="regionPageFeaturedMissionsNormalTd regionPageFeaturedMissionsOptionalCol">
              {mission.timeFirstAvail}
            </td>
            <td className="regionPageFeaturedMissionsNormalTd regionPageFeaturedMissionsOptionalCol">
              {mission.timeLastAvail}
            </td>
            <td className="regionPageFeaturedMissionsNormalTd regionPageFeaturedMissionsOptionalCol">
              {mission.datasetCount}
            </td>
        </tr>
      )}
      </tbody>
    </table>
  );
}


/**
 * Contents of the "All Datasets" tab on the Regions page
 *
 * @param props.regionData Backend response describing the region
 * @param props.regionID of the region node in the heliodata system.
 * @param props.rawFacetResricts Facet restrictions from the page URL,
 * which may be undefined.
 * @param props.pageNum Page number to display, which may be undefined.
 * @return JSX component
 */
function RegionAllDatasets(props) {
    const {
        regionData,
        regionId,
        facetRestricts,
        rawFacetRestricts,
        pageNum,
        requestTimeToComplete,
        navigate,
        unfilteredData
    } = props;
    const searchData = regionData.searchData;
    const [currentQuery, setCurrentQuery] = useState("");

    const searchCurrentQuery = () => {
        if (currentQuery && currentQuery.length > 0) {
            const regionSlug = regionData?.parentRegions?.[0]?.slug;
            const regionPrefix = regionSlug ? `${regionSlug}.` : "";
            const adjustedQuery = `${currentQuery} region:"${regionPrefix}${regionId ?? ""}"`;
            const safeQuery = encodeURIComponent(adjustedQuery);

            if (safeQuery.length > 0) {
                navigate(`/search/${safeQuery}/1`);
            }
        }
    };


    return (
          <div className="regionPageDatasetsContainer">
            <div className="regionPageDatasetsLeft">
              {/* Facets sidebar */}
              <SearchResultsFacet
                facetData={searchData?.facets}
                defaultFacetData={unfilteredData?.searchData?.facets}
                instruments={unfilteredData?.searchData?.facets?.facet_fields?.instrument_facet_cs}
                measurements={unfilteredData?.searchData?.facets?.facet_fields?.measurement_type_cs}
                eras={unfilteredData.searchData?.facets?.facet_fields?.eras}
                timeResolutions={unfilteredData?.searchData?.facets?.facet_fields?.cadence_cat}
                regions={unfilteredData.searchData?.facets?.facet_fields?.region}
                facetRestricts={facetRestricts}
                numFound={searchData.numFound}
                customTitle={"Refine Datasets"}
                skipRegionsFacet={true}
                navigate={navigate}
                regionTaxonomyTitles={searchData.regionTaxonomyTitles}
                getNavigateUrl={
                    newQueryString =>
                        `/region/${regionId}/1/${newQueryString}/?scrollToTop=0`}
              />
            </div>

            <div className="regionPageDatasetsRight">
              {/* Search bar for searching region's datsets */}

              <div className="regionPageDatasetsSearch">
                <div className="regionElementWide">
                  <SearchBar
                    currentQuery={currentQuery}
                    setCurrentQuery={setCurrentQuery}
                    searchCurrentQuery={searchCurrentQuery}
                    navigate={navigate}
                    noControls={true}
                    whiteBg={true}
                    mediumLength={true}
                  />
                </div>
              </div>

              {/* Table of datasets, using the search results component */}
              <SearchResultsTable
                searchData={searchData}
                query={currentQuery}
                rawFacetRestricts={rawFacetRestricts}
                url={`/heliodata/app/load_region_page`}
                pageNum={pageNum}
                regionId={regionId}
                requestTimeToComplete={requestTimeToComplete}
                bottomVariant={true}
              />
            </div>
          </div>
    );
}
